<template>
	<div class="shop">
		<template v-if="piggyBankOffers || provisionOffers">
			<div
				v-for="offer in [
					...(piggyBankOffers ?? []),
					...(provisionOffers ?? []),
					...(welcomeBackOffer ? [welcomeBackOffer] : []),
				]"
				:key="offer.store_id"
				class="shop__item"
				:class="{
					'shop__item--purchased':
						offer.attributes?.limit &&
						offer.num_of_purchases >= offer.attributes.limit,
				}"
			>
				<PremiumShopItem
					v-if="offer"
					:offer="offer"
					@openPopup="handlePopupOpen(offer.store_id)"
				/>
			</div>
		</template>

		<div
			class="shop__item"
			v-for="offer in specialOffers"
			:key="offer.store_id"
		>
			<PremiumShopItem
				:offer="offer"
				@openPopup="handlePopupOpen(offer.store_id)"
			/>
		</div>
	</div>
</template>

<script lang="ts" setup>
import PremiumShopItem from "@/components/shop/cards/PremiumShopItem.vue";
import useSocketService from "@/hooks/useSocketService";
import {
	OfferAnalyticsClickSource,
	OfferAnalyticsType,
} from "@/libs/SocketService";
import type { ExtendedPiggyBankOffer } from "@/services/OfferProvider";
import { OfferProvider } from "@/services/OfferProvider";
import { PaymentProvider } from "@/services/PaymentProvider";
import useShopStore from "@/store/useShopStore";
import { storeToRefs } from "pinia";
import { computed } from "vue";

const shopStore = useShopStore();
const { specialOffers, provisionOffers, welcomeBackOffer } =
	storeToRefs(shopStore);

const piggyBankOffers = computed((): Array<ExtendedPiggyBankOffer> | null => {
	return OfferProvider.extendedPiggyBankOffers.value || null;
});

const handlePopupOpen = (storeId: string): void => {
	useSocketService().emitOfferAnalyticsSave({
		type: OfferAnalyticsType.OFFER_CLICK,
		clickSource: OfferAnalyticsClickSource.STORE ?? "UNKNOWN",
		storeId: storeId ?? "UNKNOWN",
		offerName: shopStore.getItemByStoreId(storeId)?.name ?? "UNKNOWN",
	});

	PaymentProvider.openPopup(storeId, {
		clickSource: OfferAnalyticsClickSource.STORE,
	});
};
</script>

<style lang="scss" scoped>
.shop {
	&__item {
		position: relative;
		width: 100%;

		&--purchased {
			order: 1; // RAKO: Purchased items are moved to the bottom
		}
	}

	&-section {
		position: relative;
		@include flex-row-center;
		flex-wrap: wrap;
		gap: 1rem;
		width: 100%;

		&--sale {
			margin: 1rem 0;
		}

		.ref-section-wrapper {
			position: relative;
			width: 100%;
		}
		&__toast-message-buy {
			position: absolute;
			top: 50%;
			left: 40%;
		}
	}
}

.section-label {
	@include flex-row-center;
	position: relative;
	mask-image: linear-gradient(
		to right,
		transparent 2%,
		black 20%,
		black 80%,
		transparent 98%
	);
	width: 100%;
	height: 1.875rem;

	span {
		position: relative;
		z-index: 1;
		padding-bottom: 0.25rem;
		color: #f4d278;
		font-weight: 800;
		font-size: 1.25rem;
		line-height: 1.25rem;
		text-align: center;
		text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
	}

	&::before {
		position: absolute;
		top: 0;
		left: 0;
		@include background(
			url($path-textures + "silk-flag-texture-bg.avif"),
			cover,
			center center,
			no-repeat
		);
		z-index: 0;
		width: 100%;
		height: 100%;
		content: "";
	}

	&::after {
		position: absolute;
		top: 0;
		left: 0;
		mix-blend-mode: hard-light;
		background: rgba(78, 29, 97, 1);
		width: 100%;
		height: 100%;
		content: "";
	}
}

.text-button {
	:deep(.text-button) {
		gap: 0.5rem;
		padding: 0 1rem;
		width: unset;

		.dual-price {
			opacity: 0.9;
			font-size: 0.75rem;
		}
	}
}
</style>
